/**
 * Light mode's background: a mild grid of dots joined by fine lines, like
 * graph paper under the page (owner, 2026-10-23; dark mode has the starfield
 * instead). Shown on platform pages only, never at checkout or on a seller's
 * page (src/lib/cosmic-effects.ts).
 *
 * TWO HALVES THAT MUST MEET. The still grid is this CSS background, on a layer
 * as tall as the document, so it scrolls with the page for nothing, draws on
 * a phone without any script, and holds still for a visitor who asked for
 * reduced motion. Where the mouse pushes dots aside, a canvas over it
 * (src/lib/dot-grid-ripple.ts) paints those tiles in the page colour and draws
 * them again with the dots moved, so its lines must land on exactly the
 * pixels the CSS uses. Both read the numbers below; dotGridPoint is where
 * either puts a dot.
 *
 * Kept tiny: ClientLayout imports this up front, and everything that moves
 * loads only once a mouse is seen (src/components/ui/dot-grid.tsx).
 */

/**
 * One dot every DOT_GRID_SPACING CSS px each way. A multiple of 4, so a tile
 * edge falls on a whole device pixel at 1.25x, 1.5x and 1.75x as well as 2x,
 * where the canvas meets the CSS.
 */
export const DOT_GRID_SPACING = 32;
/** Where a line runs in its tile: it covers the whole pixel row (or column) [LINE_AT, LINE_AT + 1]. */
const LINE_AT = DOT_GRID_SPACING / 2;
/** A dot sits on the middle of both lines, so they cross under it. */
const DOT_AT = LINE_AT + 0.5;
/** The dot: solid to DOT_CORE px from its centre, faded out by DOT_EDGE. */
const DOT_CORE = 0.75;
const DOT_EDGE = 1.75;

/** Zinc-800, the ink the light-mode stars were drawn in, as r, g, b. */
export const DOT_GRID_INK = [39, 39, 42] as const;
/** Mild and dull (owner): the dots just visible on the page colour, the lines barely. */
export const DOT_GRID_DOT_ALPHA = 0.2;
export const DOT_GRID_LINE_ALPHA = 0.055;
const INK = DOT_GRID_INK.join(', ');
export const DOT_GRID_DOT_COLOR = `rgba(${INK}, ${DOT_GRID_DOT_ALPHA})`;
export const DOT_GRID_LINE_COLOR = `rgba(${INK}, ${DOT_GRID_LINE_ALPHA})`;

/** Where dot (col, row) sits, CSS px from the grid layer's top-left corner. */
export function dotGridPoint(col: number, row: number): { x: number; y: number } {
  return { x: col * DOT_GRID_SPACING + DOT_AT, y: row * DOT_GRID_SPACING + DOT_AT };
}

/** The dot's soft edge, for the canvas to draw the same dot: solid to `core`, gone by `edge`, CSS px. */
export const DOT_GRID_DOT_RADII = { core: DOT_CORE, edge: DOT_EDGE } as const;

/**
 * The still grid as a CSS background, one tile per DOT_GRID_SPACING, tiled
 * from the layer's top-left corner: the dot over two 1 px lines.
 */
export const DOT_GRID_BACKGROUND = {
  backgroundImage: [
    `radial-gradient(circle at ${DOT_AT}px ${DOT_AT}px, ${DOT_GRID_DOT_COLOR} ${DOT_CORE}px, transparent ${DOT_EDGE}px)`,
    `linear-gradient(to right, transparent ${LINE_AT}px, ${DOT_GRID_LINE_COLOR} ${LINE_AT}px, ${DOT_GRID_LINE_COLOR} ${LINE_AT + 1}px, transparent ${LINE_AT + 1}px)`,
    `linear-gradient(to bottom, transparent ${LINE_AT}px, ${DOT_GRID_LINE_COLOR} ${LINE_AT}px, ${DOT_GRID_LINE_COLOR} ${LINE_AT + 1}px, transparent ${LINE_AT + 1}px)`,
  ].join(', '),
  backgroundSize: `${DOT_GRID_SPACING}px ${DOT_GRID_SPACING}px`,
  backgroundPosition: '0 0',
} as const;
